import { ActivatedRouteSnapshot, Router, UrlSegment } from '@angular/router';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    constructor(private router: Router) {}

    /**
     * Collects all URL segments from the current router state.
     *
     * This utility is intended for use in services or other non-component
     * contexts where `ActivatedRoute` cannot be injected.
     * It traverses the router state's snapshot tree starting from the root
     * and aggregates URL segments from all nested routes.
     *
     * Useful when you need full URL information outside of components
     * (e.g., facades, effects, guards, or shared services).
     */
    getAllUrlSegments(): UrlSegment[] {
        const collect = (route: ActivatedRouteSnapshot, acc: UrlSegment[] = []): UrlSegment[] => {
            acc.push(...route.url);

            for (const child of route.children) {
                collect(child, acc);
            }

            return acc;
        };

        return collect(this.router.routerState.snapshot.root);
    }

    /**
     * Returns the URL segment that follows the given segment name.
     *
     * This helper is useful in services or other non-component code
     * where `ActivatedRoute` is not available and route parameters
     * cannot be accessed directly.
     *
     * Example:
     *   URL: /users/42/details
     *   getNextUrlSegment('users') -> '42'
     *
     * Returns `null` if the segment is not found, or if it is the last segment.
     */
    getNextUrlSegment(segmentName: string): string | null {
        const segments = this.getAllUrlSegments();
        const index = segments.findIndex((s) => s.path === segmentName);
        return index !== -1 ? segments[index + 1]?.path ?? null : null;
    }
}
